<template>
  <header class="v2-topbar">
    <div class="v2-topbar__left">
      <button
        type="button"
        class="v2-topbar__hamburger"
        aria-label="Abrir menú de navegación"
        @click="$emit('toggle-sidebar')"
      >
        <v-icon size="24">mdi-menu</v-icon>
      </button>

      <div
        v-if="hasHeadOfficeLogo"
        class="v2-topbar__head-office"
      >
        <img
          :src="headOfficeLogo"
          :alt="headOfficeName"
          class="v2-topbar__head-office-img"
        />
        <span class="v2-topbar__head-office-name">{{ headOfficeName }}</span>
      </div>
    </div>

    <div class="v2-topbar__right">
      <router-link
        v-if="user && user.name"
        :to="{ name: 'Member Dashboard V2 Profile' }"
        class="v2-topbar__user"
        :aria-label="`Ir a mi perfil (${user.name})`"
      >
        <div class="v2-topbar__avatar" aria-hidden="true">
          <img
            v-if="hasProfilePicture"
            :src="user.profilePicture"
            :alt="user.name"
          />
          <span v-else class="v2-topbar__avatar-initials">{{ initials }}</span>
        </div>
        <div class="v2-topbar__user-info">
          <p class="v2-topbar__user-name">{{ user.name }}</p>
          <p v-if="user.email" class="v2-topbar__user-email">{{ user.email }}</p>
        </div>
        <v-icon size="16" class="v2-topbar__user-chevron">mdi-chevron-right</v-icon>
      </router-link>
    </div>
  </header>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "AppTopbar",
  props: {
    title: {
      type: String,
      default: "Inicio",
    },
  },
  computed: {
    ...mapState("user", ["user"]),
    hasProfilePicture() {
      if (!this.user || !this.user.profilePicture) return false;
      const v = this.user.profilePicture;
      return v !== "NOT_ASSIGNED" && v !== "N/D";
    },
    initials() {
      const name = (this.user && (this.user.name || this.user.company)) || "N/D";
      return String(name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";
    },
    headOfficeObject() {
      if (!this.user || !this.user.headOffice) return null;
      return typeof this.user.headOffice === "object" ? this.user.headOffice : null;
    },
    headOfficeLogo() {
      const head = this.headOfficeObject;
      if (!head) return null;
      const candidate = head.profilePicture || head.logo || head.image || head.banner;
      if (!candidate || candidate === "N/D" || candidate === "NOT_ASSIGNED") return null;
      return candidate;
    },
    hasHeadOfficeLogo() {
      return Boolean(this.headOfficeLogo);
    },
    headOfficeName() {
      const head = this.headOfficeObject;
      if (!head) return "HeadOffice";
      return head.name || head.company || head.legalName || "HeadOffice";
    },
  },
};
</script>

<style scoped>
.v2-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  height: var(--layout-topbar-height);
  padding: 0 var(--space-6);
  background-color: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: var(--z-overlay);
  transition: background-color var(--transition-base), box-shadow var(--transition-base), left var(--transition-base);
}

.v2-topbar__left {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  flex: 1 1 auto;
}

.v2-topbar__hamburger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: transparent;
  border: 0;
  color: var(--color-text);
  cursor: pointer;
  transition: background-color var(--transition-fast);
  flex-shrink: 0;
}

.v2-topbar__hamburger:hover {
  background-color: var(--color-neutral-100);
}

.v2-topbar__hamburger:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-topbar__right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.v2-topbar__user {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  margin-left: var(--space-2);
  border-left: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.v2-topbar__user:hover {
  background-color: var(--color-neutral-100);
  color: var(--color-text);
}

.v2-topbar__user:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-topbar__user:hover .v2-topbar__user-chevron {
  color: var(--color-amber);
}

.v2-topbar__user-chevron {
  color: var(--color-text-subtle);
  transition: color var(--transition-fast);
}

.v2-topbar__avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background-color: var(--color-neutral-100);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.v2-topbar__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.v2-topbar__avatar-initials {
  font-family: var(--font-headline);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  color: var(--color-navy-deep);
  line-height: 1;
}

.v2-topbar__user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  min-width: 0;
}

.v2-topbar__user-name {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  line-height: var(--lh-tight);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-topbar__user-email {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-label-xs);
  color: var(--color-text-muted);
  line-height: var(--lh-tight);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 1024px) {
  .v2-topbar {
    padding: 0 var(--space-8);
    left: var(--layout-sidebar-expanded);
  }

  .v2-topbar__hamburger {
    display: none;
  }
}

@media (max-width: 767.98px) {
  .v2-topbar__user-info {
    display: none;
  }

  .v2-topbar__head-office {
    margin-left: 0;
  }

  .v2-topbar__head-office-name {
    display: none;
  }

  .v2-topbar__head-office-img {
    width: 36px;
    height: 36px;
  }
}

.v2-topbar__head-office {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  margin-left: var(--space-2);
  flex-shrink: 1;
}

.v2-topbar__head-office-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  display: block;
  border-radius: var(--radius-full);
  background-color: var(--color-neutral-100);
  flex-shrink: 0;
}

.v2-topbar__head-office-name {
  font-family: var(--font-headline);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  line-height: var(--lh-tight);
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .v2-topbar__head-office {
    display: inline-flex;
  }
}
</style>
